package org.example.project

import androidx.compose.runtime.mutableStateOf
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

class SettingsViewModel(private val scope: CoroutineScope) {
    private val _taxCountriesList = MutableStateFlow<List<String>>(emptyList())
    val taxCountriesList: StateFlow<List<String>> = _taxCountriesList.asStateFlow()

    private val _fiatPaymentMethodsList = MutableStateFlow<List<String>>(emptyList())
    val fiatPaymentMethodsList: StateFlow<List<String>> = _fiatPaymentMethodsList.asStateFlow()

    private val _licensePackages = MutableStateFlow<List<LicensePackageDocument>>(emptyList())
    val licensePackages: StateFlow<List<LicensePackageDocument>> = _licensePackages.asStateFlow()

    private val _iapPackages = MutableStateFlow<List<IapPackageDocument>>(emptyList())
    val iapPackages: StateFlow<List<IapPackageDocument>> = _iapPackages.asStateFlow()

    private val _suggestedLicenseUnits = MutableStateFlow<List<Int>>(emptyList())
    val suggestedLicenseUnits: StateFlow<List<Int>> = _suggestedLicenseUnits.asStateFlow()

    private val _orgLevels = MutableStateFlow<List<OrgTierDocument>>(emptyList())
    val orgLevels: StateFlow<List<OrgTierDocument>> = _orgLevels.asStateFlow()

    private val _licenseKeys = MutableStateFlow<List<LicenseKeyDocument>>(emptyList())
    val licenseKeys: StateFlow<List<LicenseKeyDocument>> = _licenseKeys.asStateFlow()

    var isLoadingCodes = mutableStateOf(false)
    var isMoreLoading = mutableStateOf(false)
    var nextCursor = mutableStateOf<String?>(null)
    var selectedStatus = mutableStateOf<LicenseKeyStatus?>(null)

    init {
        fetchTaxAndFiatData()
    }

    fun onStatusFilterChanged(status: LicenseKeyStatus?) {
        selectedStatus.value = status
        loadLicenseKeys(isRefresh = true)
    }

    private fun fetchTaxAndFiatData() {
        scope.launch {
            when (val result = FirebaseManager.getTaxCountries()) {
                is GetTaxCountriesResult.Success -> _taxCountriesList.value = result.data
                is GetTaxCountriesResult.Error -> println("Failed to load tax countries")
            }
        }
        scope.launch {
            when (val result = FirebaseManager.getFiatPaymentMethods()) {
                is GetFiatPaymentMethodsResult.Success -> _fiatPaymentMethodsList.value = result.data
                is GetFiatPaymentMethodsResult.Error -> println("Failed to load fiat methods")
            }
        }
    }

    fun loadOrgData() {
        loadLicensePackages()
        loadIapPackages()
        loadOrgLevels()
        loadLicenseKeys(isRefresh = true)
    }

    private fun updateSuggestedLicenseUnits() {
        val wp = _licensePackages.value.map { it.bonus_units }
        val rp = _iapPackages.value.map { it.iap_units }
        _suggestedLicenseUnits.value = (wp + rp).filter { it > 0 }.distinct().sorted()
    }

    private fun loadLicensePackages() {
        scope.launch {
            val packsResult = FirebaseManager.getLicensePackages()
            if (packsResult is LicensePackageListResult.Success) {
                _licensePackages.value = packsResult.data
                updateSuggestedLicenseUnits()
            }
        }
    }

    private fun loadIapPackages() {
        scope.launch {
            val packsResult = FirebaseManager.getIapPackages()
            if (packsResult is GetIapPackagesResult.Success) {
                _iapPackages.value = packsResult.data
                updateSuggestedLicenseUnits()
            }
        }
    }

    private fun loadOrgLevels() {
        scope.launch {
            val levelsResult = FirebaseManager.getOrgLevels()
            if (levelsResult is OrgLevelListResult.Success) {
                _orgLevels.value = levelsResult.data
            }
        }
    }

    fun loadLicenseKeys(isRefresh: Boolean = true) {
        if (isRefresh && isLoadingCodes.value) return
        if (!isRefresh && (isMoreLoading.value || nextCursor.value == null)) return

        scope.launch {
            if (isRefresh) isLoadingCodes.value = true else isMoreLoading.value = true

            val currentCursor = if (isRefresh) null else nextCursor.value
            val codesResult = FirebaseManager.listLicenseKeys(
                limit = 10, 
                status = selectedStatus.value,
                cursor = currentCursor
            )

            if (isRefresh) {
                _licenseKeys.value = codesResult.data
            } else {
                _licenseKeys.value = _licenseKeys.value + codesResult.data
            }
            nextCursor.value = codesResult.next_cursor

            isLoadingCodes.value = false
            isMoreLoading.value = false
        }
    }    
    fun removeRevokedCode(codeSn: String) {
        _licenseKeys.value = _licenseKeys.value.filterNot { it.key_sn == codeSn }
    }
}
